import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import SeoHead from "@/components/feature/SeoHead";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles, getCategory } from "@/mocks/content";

const origin = "https://zuori-chigua.example";

export default function ArticleDetail() {
  const { slug } = useParams();
  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);

  if (!article) {
    return (
      <>
        <Header />
        <main className="mx-auto max-w-3xl px-4 py-24 text-center">
          <p className="font-display text-6xl font-black text-background-300">404</p>
          <h1 className="mt-4 font-display text-2xl font-bold text-foreground-900">这篇复盘不见了</h1>
          <p className="mt-2 text-foreground-600">内容可能已被移动或归档，回到速递页看看吧。</p>
          <Link
            to="/news"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" aria-hidden="true" /> 返回瓜田速递
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const cat = getCategory(article.category);
  const related = articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3);
  const cover = article.wideCover ?? article.cover;

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: origin + "/" },
        { "@type": "ListItem", position: 2, name: "瓜田速递", item: origin + "/news" },
        { "@type": "ListItem", position: 3, name: cat.name, item: origin + "/news?c=" + article.category },
        { "@type": "ListItem", position: 4, name: article.title, item: origin + "/article/" + article.slug },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.summary,
      image: [cover],
      author: { "@type": "Organization", name: article.author },
      publisher: { "@type": "Organization", name: "昨日吃瓜" },
      datePublished: "2026-" + article.date,
      dateModified: "2026-" + article.date,
      mainEntityOfPage: origin + "/article/" + article.slug,
      inLanguage: "zh-CN",
    },
  ];

  return (
    <>
      <SeoHead
        title={article.title + "｜昨日吃瓜"}
        description={article.summary + " 深入昨日吃瓜细节分析，以公开信息为准，理性吃瓜。"}
        keywords={article.tags.join(",") + ",昨日吃瓜细节分析,瓜田隐藏线索"}
        path={"/article/" + article.slug}
        ogType="article"
        ogImage={cover}
        jsonLd={schema}
      />
      <Header />
      <main className="bg-background-50">
        {/* hero */}
        <div className="border-b border-background-300/60 bg-background-100/70">
          <div className="mx-auto max-w-4xl px-4 py-10 md:px-6 md:py-14">
            <nav className="mb-5 flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
              <Link to="/" className="flex items-center gap-1 hover:text-primary-600">
                <i className="ri-home-4-line" aria-hidden="true" /> 首页
              </Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
              <Link to="/news" className="hover:text-primary-600">瓜田速递</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
              <Link to={"/news?c=" + article.category} className="hover:text-primary-600">
                {cat.name}
              </Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
              <span className="max-w-[10rem] truncate text-foreground-700">{article.title}</span>
            </nav>

            <div className="flex items-center gap-2">
              <span className={`rounded-full px-3 py-1 text-xs font-medium text-background-50 ${cat.dot}`}>
                {cat.name}
              </span>
              <span className="rounded-full bg-background-50 px-3 py-1 text-xs font-medium text-foreground-800">
                {article.status}
              </span>
            </div>
            <h1 className="mt-4 font-display text-2xl font-black leading-snug text-foreground-950 md:text-4xl md:leading-tight">
              {article.title}
            </h1>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">{article.summary}</p>

            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-background-300/60 pt-5 text-sm text-foreground-500">
              <span className="flex items-center gap-2 font-medium text-foreground-700">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-edit-line" aria-hidden="true" />
                </span>
                {article.author}
              </span>
              <span>发布于 2026-{article.date}</span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line" aria-hidden="true" /> 阅读约 {article.readMin} 分钟
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" aria-hidden="true" /> {(article.views / 10000).toFixed(1)}w 围观
              </span>
            </div>
          </div>
        </div>

        {/* body */}
        <article className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-14">
          <div className="relative overflow-hidden rounded-2xl">
            <img
              src={cover}
              alt={article.title + " 复盘配图"}
              className="h-64 w-full object-cover md:h-80"
            />
            <span className="absolute left-4 top-4 rounded-md bg-background-50/90 px-3 py-1 text-xs font-medium text-foreground-800">
              昨日瓜田 · 深度复盘
            </span>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-2">
            {article.tags.map((t) => (
              <span
                key={t}
                className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800"
              >
                # {t}
              </span>
            ))}
          </div>

          {article.paragraphs.map((block, i) => (
            <section key={i} className="mt-7">
              {block.heading && (
                <h2 className="mb-3 flex items-center gap-2 font-display text-xl font-black text-foreground-950 md:text-2xl">
                  <span className="flex h-6 items-center rounded bg-primary-500 px-2 text-xs text-background-50">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {block.heading}
                </h2>
              )}
              {block.body.map((para, j) => (
                <p
                  key={j}
                  className={
                    "mb-4 text-[15px] leading-8 text-foreground-800 md:text-base md:leading-9 " +
                    (i === 0 && j === 0 ? "dropcap" : "")
                  }
                >
                  {para}
                </p>
              ))}
            </section>
          ))}

          <aside className="mt-8 rounded-xl border border-accent-200 bg-accent-50 p-5">
            <p className="flex items-center gap-2 font-semibold text-accent-900">
              <i className="ri-shield-check-line" aria-hidden="true" /> 编辑部说明
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">
              本篇为公开信息的整理与观察，涉及无法核实的人与事均标注「待证」，不代表任何定论，请以当事人及官方正式回应为准。理性围观，也请理性发言。
            </p>
          </aside>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-6">
            <span className="text-sm font-semibold text-foreground-800">觉得有用？分享给同样在吃瓜的朋友</span>
            <div className="flex items-center gap-2 text-background-50">
              {[
                { i: "ri-weibo-line", l: "微博" },
                { i: "ri-wechat-line", l: "微信" },
                { i: "ri-clipboard-line", l: "复制链接" },
              ].map((s, k) => (
                <button
                  key={k}
                  type="button"
                  title={"分享到" + s.l}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 transition-colors hover:bg-primary-600"
                >
                  <i className={s.i} aria-hidden="true" />
                </button>
              ))}
            </div>
          </div>
        </article>

        {/* related */}
        {related.length > 0 && (
          <section className="border-t border-background-300/60 bg-background-100/60">
            <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="font-display text-2xl font-black text-foreground-950">
                  同栏目相关复盘
                </h2>
                <Link
                  to={"/news?c=" + article.category}
                  className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  更多{cat.name} <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}